import { useTooltipData } from '../../../core/components/overlay-tooltip/hooks/useTooltipData.js';
import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { Data } from '../../../core/components/overlay-tooltip/types.js';
import { isTooltipDataEmpty } from '../../../core/components/overlay-tooltip/utils.js';
import type { SeriesActionsTemplate } from '../../../core/types/series-actions-template.js';
import { useHoneycombCustomTooltip } from '../../hooks/use-honeycomb-custom-tooltip.js';
import { useHoneycombTruncationMode } from '../../hooks/use-honeycomb-truncation-mode.js';
import type {
  HoneycombTooltipMetadata,
  HoneycombTooltipPayload,
} from '../../types/honeycomb-internal.js';
import type { HoneycombTileData } from '../../types/honeycomb.js';

type SeriesData = Data['series'][number];

interface HoneycombOverlayTooltipProps {
  actionsForItem?: (
    item: SeriesData,
  ) => (() => SeriesActionsTemplate) | undefined;
}

export const HoneycombOverlayTooltip = (
  props: HoneycombOverlayTooltipProps,
) => {
  const data = useTooltipData().data;
  const truncationMode = useHoneycombTruncationMode();
  const tooltip = useHoneycombCustomTooltip();

  if (tooltip?.hidden || isTooltipDataEmpty(data)) {
    return null;
  }

  const series = data.series[0];

  if (typeof tooltip?.template === 'function') {
    const metadata = data.metadata as
      | {
          id: string;
          tileData: HoneycombTileData;
          tooltipMetadata: HoneycombTooltipMetadata;
        }
      | undefined;

    if (metadata) {
      const payload: HoneycombTooltipPayload = {
        name: metadata.tooltipMetadata.name,
        value: metadata.tileData.value,
        formattedValue: String(series.value),
        color: metadata.tooltipMetadata.color.Default,
        data: metadata.tileData,
      };

      return <OverlayTooltip legacyTemplate={tooltip.template(payload)} />;
    }
  }

  return (
    <OverlayTooltip truncation={truncationMode}>
      <OverlayTooltip.SeriesItem
        name={series.name}
        value={series.value}
        color={series.color}
        actions={props.actionsForItem?.(series)}
      />
    </OverlayTooltip>
  );
};

HoneycombOverlayTooltip['displayName'] = 'HoneycombOverlayTooltip';
